import React from "react";
import { Link } from "react-router-dom";
import "./Pricing.css";
import FAQ from "../FAQ";
import Footer from "../Footer";
import { CheckIcon } from "../public/icons";
import {
  formatSubscriptionInterval,
  formatSubscriptionPrice,
  getPublishedSubscriptionPlan,
} from "../../constants/subscriptionPlans";

const singleModePlan = getPublishedSubscriptionPlan("single");
const teamModePlan = getPublishedSubscriptionPlan("team");

const plans = [
  {
    key: "single",
    name: "Just me",
    badge: "Most people",
    plan: singleModePlan,
    description: "Everything one person needs to be bookable and get paid.",
    features: [
      "Your own booking page and link",
      "Unlimited services and appointments",
      "Deposits and upfront payment via Stripe",
      "Client records, notes and visit history",
      "Automated email reminders and follow-ups",
      "Cancellation policy you set yourself",
    ],
    cta: "Start free trial",
    to: "/signup?mode=single",
    ctaClassName: "btn btn-primary btn-block btn-md",
  },
  {
    key: "team",
    name: "With a team",
    plan: teamModePlan,
    description: "One workspace for a suite, salon or studio with more than one pair of hands.",
    features: [
      "Everything in Just me",
      "Team profiles with roles and permissions",
      "Shared services assigned per provider",
      "Individual calendars, one team view",
      "Payouts to the business or straight to the provider",
    ],
    cta: "Start team trial",
    to: "/signup?mode=team",
    ctaClassName: "btn btn-secondary btn-secondary--accent btn-block btn-md",
    highlighted: true,
  },
];

const pricingFaqs = [
  {
    question: "Is there a free trial?",
    answer:
      "Yes. You get the full product during the trial, including payments, and you're not charged until it ends.",
  },
  {
    question: "What happens to my bookings if I cancel?",
    answer:
      "Existing bookings stay put and your clients can still manage them. Your page stops accepting new bookings.",
  },
  {
    question: "Do I need Stripe to take bookings?",
    answer:
      "Not for free or pay-in-person bookings. You need it to charge deposits or take payment online.",
  },
  {
    question: "Can I bring my clients and calendar from another app?",
    answer:
      "Yes. On Acuity, paste your booking page link and we copy your services, add-ons, booking questions, photos, colors and intro; your clients and appointments come from files you export. Vagaro, Square Appointments, Fresha, GlossGenius and Booksy all export files you can upload, and any other app works through a spreadsheet. You see what will be added before anything is saved, your clients are never emailed about it, and you can undo the whole thing.",
  },
  {
    question: "Can I start solo and add people later?",
    answer:
      "Yes. The owner can move the account to Team Mode from billing settings whenever a second chair appears.",
  },
];

const Pricing = () => (
  <>
    <main className="pricing-page">
      <section className="site-section site-section--hero pricing-hero">
        <span className="deco deco--2 pricing-hero__deco" aria-hidden="true" />
        <div className="site-inner">
          <div className="site-intro pricing-hero__copy">
            <p className="eyebrow">Pricing</p>
            <h1>One price. No percentage of your work.</h1>
            <p className="lead">
              We don't take a cut of your bookings. You pay a flat monthly fee and keep the rest,
              minus what the card networks charge.
            </p>
          </div>
        </div>
      </section>

      <section className="site-section site-section--flush-top pricing-plans">
        <div className="site-inner">
          <div className="pricing-grid">
            {plans.map(({ key, name, badge, plan, description, features, cta, to, ctaClassName, highlighted }) => (
              <article
                className={`pricing-card${highlighted ? " pricing-card--highlighted" : ""}`}
                key={key}
                aria-labelledby={`pricing-plan-${key}`}
              >
                <div>
                  <div className="pricing-card__title-row">
                    <h2 className="pricing-card__name" id={`pricing-plan-${key}`}>
                      {name}
                    </h2>
                    {badge && <span className="tag tag-sage tag-upper">{badge}</span>}
                  </div>
                  <p className="pricing-card__price">
                    <span className="pricing-card__amount">
                      {formatSubscriptionPrice(plan.price, "en-US")}
                    </span>
                    <span className="pricing-card__cadence">
                      / {formatSubscriptionInterval(plan.price)}
                    </span>
                  </p>
                  <p className="pricing-card__description">{description}</p>
                </div>
                <ul className="check-list check-list--compact pricing-card__features">
                  {features.map((feature) => (
                    <li key={`${key}-${feature}`}>
                      <CheckIcon size={18} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link className={ctaClassName} to={to}>
                  {cta}
                </Link>
              </article>
            ))}
          </div>
          <p className="pricing-note">
            Plus Stripe's processing fee on each payment. Haastia takes no application fee from
            your bookings. Payment routing does not calculate payroll, commission or tax.
          </p>
        </div>
      </section>

      <section className="site-section site-section--tinted pricing-faq">
        <div className="site-inner">
          <h2 className="pricing-faq__title">Questions people ask before paying</h2>
          <div className="faq-list pricing-faq__list">
            {pricingFaqs.map((faq) => (
              <FAQ key={faq.question} question={faq.question} answer={faq.answer} variant="bg" />
            ))}
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default Pricing;
